import { IInsiderSimplifiedBenefit } from "@faire/deprecated-retailer-visitor-shared/hooks/insider/useInsiderAdvertisedBenefits";
import { isLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { Core, Program } from "@faire/slate/foundation/color";
import { Close } from "@faire/slate/icons/Close";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate-deprecated/Button";
import { IconButton } from "@faire/web/slate-deprecated/IconButton";
import { Insider } from "@faire/web/slate/icons/InsiderIcon";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import {
  chunkTransformations,
  StrictLocalMsg,
} from "@faire/web/ui/lib/localization";
import React from "react";
import styled from "styled-components";

type InsiderSlimBannerType = "PAUSED" | "UNENROLLED";

export interface InsiderSlimBannerProps {
  type: InsiderSlimBannerType;
  benefits: IInsiderSimplifiedBenefit[];
  membershipState: keyof typeof ACTIONS;
  onAction: () => void;
  onClose: () => void;
}

export const InsiderSlimBanner = React.memo<InsiderSlimBannerProps>(
  ({ type, benefits, membershipState, onAction, onClose }) => {
    const Action: React.FC = ACTIONS[membershipState];
    const { strictLocalize } = useStrictLocalization();

    return (
      <InsiderSlimBannerWrapper
        $useUpdatedSpacing={isLoggedInRetailer()}
        data-test-id="insider-slim-banner"
      >
        <Row gap="2x" align="center">
          <InsiderLogo />
          <Typography variant="paragraphSansRegular">
            {getBenefitText(type, benefits)}{" "}
            <Button
              variant="tertiary"
              onClick={onAction}
              data-test-id="insider-slim-banner-cta"
            >
              <Action />
            </Button>
          </Typography>
        </Row>

        <StyledIconButton
          variant="tertiary"
          aria-label={strictLocalize({
            id: "diVxpc",
            defaultMessage: "close button",
            description: {
              text: "Close button for the banner",
            },
          })}
          onClick={onClose}
          data-test-id="insider-slim-banner-close-button"
          size="small"
        >
          <Close fontSize="14px" color={Core.icon.primary} strokeWidth={2} />
        </StyledIconButton>
      </InsiderSlimBannerWrapper>
    );
  }
);

const InsiderSlimBannerWrapper = styled(Row).attrs({
  justify: "space-between",
  align: "center",
  gap: "2x",
})<{
  $useUpdatedSpacing: boolean;
}>`
  margin-left: ${({ $useUpdatedSpacing }) =>
    $useUpdatedSpacing ? "3px" : undefined};
  background-color: ${Program.surface.insider.primary};
  padding: ${getSpacing("2x")};
`;

const StyledIconButton = styled(IconButton)`
  block-size: 14px;
  inline-size: 14px;
`;

const InsiderLogo: React.FC = () => (
  <Row align="center" gap={4}>
    <Insider
      small
      color={Program.icon.insider}
      fill={Program.icon.insider}
      fontSize="16px"
    />

    <Typography variant="paragraphSansMedium" color={Program.text.insider}>
      Insider
    </Typography>
  </Row>
);

const getBenefitText = (
  type: InsiderSlimBannerType,
  benefits: IInsiderSimplifiedBenefit[]
) => {
  const hasFreeDuties =
    benefits.includes("FREE_IMPORT_DUTIES") ||
    benefits.includes("FREE_SHIPPING_AND_DUTIES");
  if (type === "PAUSED") {
    if (hasFreeDuties) {
      return (
        <StrictLocalMsg
          id="BLruK6"
          defaultMessage="Unlock <MediumFormatting>free shipping and import duties</MediumFormatting> on thousands of brands with Insider."
          description={{
            text: "Promotional text advertising, to retailer that paused their membership, the benefits of rejoining Insider",
            img: "https://cdn.faire.com/fastly/1630f2f340bcfc6931bdb0a13e2ecdea826364210ddcfe5c7f5df372074a9bf9.png",
          }}
          values={{
            MediumFormatting: chunkTransformations.Medium,
          }}
        />
      );
    }

    return (
      <StrictLocalMsg
        id="PLU44q"
        defaultMessage="Unlock <MediumFormatting>free shipping</MediumFormatting> on thousands of brands with Insider."
        description={{
          text: "Promotional text advertising, to retailer that paused their membership, the benefits of rejoining Insider",
          img: "https://cdn.faire.com/fastly/1630f2f340bcfc6931bdb0a13e2ecdea826364210ddcfe5c7f5df372074a9bf9.png",
        }}
        values={{
          MediumFormatting: chunkTransformations.Medium,
        }}
      />
    );
  }

  if (hasFreeDuties) {
    return (
      <StrictLocalMsg
        id="ooHMOg"
        defaultMessage="Unlock <MediumFormatting>free shipping and import duties</MediumFormatting> on thousands of brands with an Insider membership."
        description={{
          text: "Promotional text advertising, to unenrolled retailer, the benefits of joining Insider",
          img: "https://cdn.faire.com/fastly/1630f2f340bcfc6931bdb0a13e2ecdea826364210ddcfe5c7f5df372074a9bf9.png",
        }}
        values={{
          MediumFormatting: chunkTransformations.Medium,
        }}
      />
    );
  }

  return (
    <StrictLocalMsg
      id="XqkFDk"
      defaultMessage="Unlock <MediumFormatting>free shipping</MediumFormatting> on thousands of brands with an Insider membership."
      description={{
        text: "Promotional text advertising, to unenrolled retailer, the benefits of joining Insider",
        img: "https://cdn.faire.com/fastly/1630f2f340bcfc6931bdb0a13e2ecdea826364210ddcfe5c7f5df372074a9bf9.png",
      }}
      values={{
        MediumFormatting: chunkTransformations.Medium,
      }}
    />
  );
};

const ACTIONS = {
  TRIAL_ELIGIBLE: () => (
    <StrictLocalMsg
      id="6sCFYY"
      defaultMessage="Start free trial"
      description={{
        text: "Insider's advertisement banner CTA text for retailers eligible to a trial membership",
        img: "https://cdn.faire.com/fastly/d12027df4aef7eb580b0e0013fe83ed9954bd569eb9f55e97b5f691e7b5b0ae5.png",
      }}
    />
  ),
  CHURNED: () => (
    <StrictLocalMsg
      id="/u7n/X"
      defaultMessage="Rejoin Insider"
      description={{
        text: "Insider's advertisement banner CTA text for retailers that have cancelled their membership",
        img: "https://cdn.faire.com/fastly/0c9d7440778718a8ae8ab38cde6725cdc9b576ac37728c90be7d8524c9498aab.png",
      }}
    />
  ),
  PAUSED: () => (
    <StrictLocalMsg
      id="DOjcFn"
      defaultMessage="Resume membership"
      description={{
        text: "Insider's advertisement banner CTA text for retailers that have paused their membership",
        img: "https://cdn.faire.com/fastly/3132910966e547b194bb16ed870889859b60274b8e1caa9f9dc5cd99e9162a3c.png",
      }}
    />
  ),
};
